import { fetchUploadStatuses } from "./file-status-monitor";

function createProductDevelopmentActivityStreamMonitorData() {
    return {
        statusUrl: null,
        pollIntervalMs: 5000,
        timerId: null,
        active: false,
        visibilityHandler: null,
        focusHandler: null,
        init() {
            this.statusUrl = this.$el.dataset.uploadStatusUrl || null;

            const interval = Number.parseInt(
                String(this.$el.dataset.uploadStatusPollInterval || this.pollIntervalMs),
                10,
            );

            this.pollIntervalMs = Number.isFinite(interval) ? interval : 5000;
            this.registerLifecycleHandlers();
            this.start();
        },
        destroy() {
            this.stop();

            if (this.visibilityHandler) {
                document.removeEventListener("visibilitychange", this.visibilityHandler);
                this.visibilityHandler = null;
            }

            if (this.focusHandler) {
                window.removeEventListener("focus", this.focusHandler);
                this.focusHandler = null;
            }
        },
        registerLifecycleHandlers() {
            this.visibilityHandler = () => {
                if (this.active && this.isPageInteractive()) {
                    this.queueNextTick(250);
                }
            };

            this.focusHandler = () => {
                if (this.active && this.isPageInteractive()) {
                    this.queueNextTick(250);
                }
            };

            document.addEventListener("visibilitychange", this.visibilityHandler);
            window.addEventListener("focus", this.focusHandler);
        },
        start() {
            if (this.active || !this.statusUrl) {
                return;
            }

            this.active = true;
            this.queueNextTick();
        },
        stop() {
            this.active = false;

            if (this.timerId) {
                window.clearTimeout(this.timerId);
                this.timerId = null;
            }
        },
        queueNextTick(delay = this.pollIntervalMs) {
            this.stop();
            this.active = true;
            this.timerId = window.setTimeout(() => this.tick(), delay);
        },
        attachmentRows() {
            return [...this.$el.querySelectorAll("[data-activity-attachment-row]")];
        },
        isPageInteractive() {
            return document.visibilityState === "visible" && document.hasFocus();
        },
        pendingFileIds() {
            return this.attachmentRows()
                .flatMap((row) => this.pendingFileIdsForRow(row))
                .filter((fileId, index, fileIds) => fileIds.indexOf(fileId) === index);
        },
        pendingFileIdsForRow(row) {
            const encoded = row.dataset.pendingFileIds || "[]";

            try {
                const fileIds = JSON.parse(encoded);

                if (!Array.isArray(fileIds)) {
                    return [];
                }

                return fileIds
                    .map((fileId) => Number.parseInt(String(fileId || "0"), 10))
                    .filter((fileId) => fileId > 0);
            } catch (_error) {
                return [];
            }
        },
        async tick() {
            if (!this.active || !this.statusUrl) {
                return;
            }

            const fileIds = this.pendingFileIds();

            if (fileIds.length === 0) {
                this.stop();
                return;
            }

            if (!this.isPageInteractive()) {
                this.queueNextTick(Math.max(this.pollIntervalMs * 2, 10000));
                return;
            }

            let statusPayloads = [];

            try {
                statusPayloads = await fetchUploadStatuses(this.statusUrl, fileIds);
            } catch (_error) {
                this.queueNextTick(Math.max(this.pollIntervalMs * 2, 10000));
                return;
            }

            const changedRowKeys = new Set();

            statusPayloads.forEach((statusPayload) => {
                const fileId = Number.parseInt(String(statusPayload?.file_id || "0"), 10);

                if (fileId <= 0) {
                    return;
                }

                const nextStatus = String(statusPayload?.status || "");
                const nextScanStatus = String(statusPayload?.scan_status || "");

                [...this.$el.querySelectorAll(`[data-upload-file-id="${fileId}"]`)].forEach((element) => {
                    if (
                        element.dataset.uploadFileStatus === nextStatus &&
                        String(element.dataset.uploadFileScanStatus || "") === nextScanStatus
                    ) {
                        return;
                    }

                    const row = element.closest("[data-activity-attachment-row]");
                    const rowKey = row?.dataset.activityRowKey || null;

                    if (rowKey) {
                        changedRowKeys.add(rowKey);
                    }
                });
            });

            changedRowKeys.forEach((rowKey) => {
                window.dispatchEvent(
                    new CustomEvent(`product-development-activity-attachment-row-refresh-${rowKey}`),
                );
            });

            this.queueNextTick(changedRowKeys.size > 0 ? 1500 : this.pollIntervalMs);
        },
    };
}

export function registerProductDevelopmentActivityStreamMonitor(Alpine) {
    Alpine.data(
        "productDevelopmentActivityStreamMonitor",
        createProductDevelopmentActivityStreamMonitorData,
    );
}
